import { includes } from 'lodash';
import { uuidv4 } from '@firebase/util';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { BreadcrumbService } from '../breadcrumb/breadcrumb.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import { ActivatedRoute, NavigationEnd, Router, } from '@angular/router';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { ZuperDialogComponent } from '@zuper/components/zuper-dialog/zuper-dialog.component';
import { HotToastService } from '@ngneat/hot-toast';
import { buttonConfigValidator, dialogConfigValidator, dropDownConfigValidator, modalConfigValidator, toastConfigValidator, validateObject } from './object-validators';
import { ZuperSdkRequestService } from './sdk-requests.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ZuperSdkDataService } from './sdk-data.service';
import { ZuperSdkEventService } from './sdk-event.service';
import { SdkCustomDialogComponent } from 'app/components/sdk-custom-dialog/sdk-custom-dialog.component';
import { ZuperSdkRouteService } from './sdk-route.service';
import { LocalStorageService } from '../localstorage.service';

@Injectable({
  providedIn: 'root'
})
export class ZuperSdkService implements OnDestroy {

  breadcrumbEvent = new Subject<any>();
  dashboardBreadcrumbEvent = new Subject<any>();
  dashboardBreadcrumbActionPerformed = new Subject<any>();
  snackBarEvent = new Subject<any>();
  childWindow: Window | null = null;

  uiComponentsCreated: any = {};
  uiComponentsIdMap: any = {};
  customButtonLocationMap: any = {};

  $subscriptions: Subscription[] = [];

  openedDialog: MatDialogRef<ZuperDialogComponent, any> | MatDialogRef<SdkCustomDialogComponent, any>;

  onFormDialogChanges = new BehaviorSubject(null);
  onFormValueUpdatesAsRequest = new BehaviorSubject(null);
  onFormConfigUpdateAsRequest = new BehaviorSubject(null);
  onCustomDialogIframeLoad = new BehaviorSubject(null);
  dispatchDataForIframeModal = new Subject();
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _breadCrumbService: BreadcrumbService,
    private _localStorage: LocalStorageService,
    private _toastService: HotToastService,
    private _utilityService: UtilityService,
    private _zuperDialogService: ZuperDialogService,
    private _zuperSdkDataService: ZuperSdkDataService,
    private _zuperSdkRequestService: ZuperSdkRequestService,
    private _zuperSdkEventService: ZuperSdkEventService,
    private _zuperSdkRouteService: ZuperSdkRouteService,
  ) {
    const $breadCrumbActionPerformed = merge(
      this._breadCrumbService.breadcrumbActionPerformed, this.dashboardBreadcrumbActionPerformed
    ).subscribe((btnConfig: any) => {
      const btnGuid = this.uiComponentsIdMap[btnConfig.id];
      const btnMessage = this.uiComponentsCreated[btnGuid];
      if (btnMessage) {
        this.onButtonClicked(btnMessage, btnConfig);
      }
    });
    this.$subscriptions.push($breadCrumbActionPerformed);
  }

  ngOnDestroy(): void {
    this.$subscriptions.forEach($sub => $sub.unsubscribe());
  }

  uid(): string {
    return Date.now().toString(36) + Math.random().toString(36);
  }

  fetchCustomSDKScript(): string {
    const baseUrl = this._localStorage.getLocalStorage('dc_api_url');
    const company = this._localStorage.getLocalStorage('company');
    const companyUid = company?.company_uid
    const authToken = this._localStorage.getLocalStorage('auth_token');
    return companyUid && authToken ? `${baseUrl}/api/misc/custom_code/code?type=JS&company_uid=${companyUid}&is_v3=true&access_token=${authToken}` : null;
  }

  connect(childWindow: Window | null): void {
    this.childWindow = childWindow;
    window.addEventListener('message', (message) => {
      if (message.data && typeof message.data === 'string' && message.origin === window.origin) {
        const m = JSON.parse(message.data);
        if (m.key && m.key === 'iframe.handshake') {
          this.sendMessage(
            childWindow,
            {
              id: null,
              key: 'zuper.app.registered',
              instanceGuid: m.instanceGuid,
              message: 'App is registered!'
            });
          this._zuperSdkEventService.initializeObjectEventListener(this.childWindow, m.instanceGuid);
        }
        else if (m.key && m.key.includes('request') && m.message.options) {
          this._zuperSdkRequestService.request(m).subscribe((data: HttpResponse<any>) => {
            const contentType = data.headers.get('Content-Type');
            let body = null;

            if (contentType && contentType.includes('application/json')) {
              body = JSON.parse(data.body || '{}'); // Safe parsing
            } else {
              body = data.body; // Handle as plain text or other formats
            }

            this.sendMessage(this.childWindow, {
              id: m.id,
              instanceGuid: m.instanceGuid,
              key: 'zuper.' + m.key + '.done',
              message: {
                body: body,
                status: data.status,
                statusText: data.statusText,
                ok: data.ok,
              }
            });
          }, (errorResponse: HttpErrorResponse) => {
            this.sendMessage(this.childWindow, {
              id: m.id,
              instanceGuid: m.instanceGuid,
              key: 'zuper.' + m.key + '.fail',
              message: {
                error: errorResponse.error,
                status: errorResponse.status,
                statusText: errorResponse.statusText,
                ok: errorResponse.ok
              }
            });
          });
        }
        else if ('request' in m) {
          const uiConfig = this.uiComponentsCreated[m.instanceGuid] ? this.uiComponentsCreated[m.instanceGuid]['config'] : null;
          if (m.request === 'invoke') {
            this.invoke(m);
          } else if (m.request === 'get') {
            if (this.uiComponentsCreated[m.instanceGuid]) {
              // check if it is modal and FORM
              if (uiConfig?.type === 'MODAL' && uiConfig?.options?.dataType === 'FORM') {
                const latestValue = this.onFormDialogChanges.value;
                const key = m.params[0];
                const payload = {};
                payload[key] = latestValue ? latestValue[key] || null : null;
                this.sendMessage(this.childWindow, {
                  id: m.id,
                  instanceGuid: m.instanceGuid,
                  result: payload
                });

              }
            } else {
              this._zuperSdkDataService.get(m, this.childWindow);
            }
          } else if (m.request === 'set') {
            // call setter function
            const data = this._utilityService.collectRouteData(this._activatedRoute.snapshot);
            if (uiConfig?.type === 'MODAL' && uiConfig?.options?.dataType === 'FORM') {
              if (this.openedDialog) {
                this.onFormValueUpdatesAsRequest.next(m.params);
              }
            } else if (this._zuperSdkEventService.checkDataKeyFormat(data['page'], m.params)) {
              this._zuperSdkEventService.castPreBuildFormPatch(m.params);
            } else if (this._zuperSdkEventService.isPreBuildModalIdAvailable(m.instanceGuid)) {
              this._zuperSdkEventService.castPreBuildFormModalPatch(m.params, m.instanceGuid, 'set');
            } else if (this._zuperSdkEventService.isPreBuildDrawerIdAvailable(m.params)) {
              this._zuperSdkEventService.castPreBuildDrawerPatchForSDK(m.params, m.instanceGuid, 'set');
            }
          } else if (m.request === 'prop') {
            if (uiConfig?.type === 'MODAL' && uiConfig?.options?.dataType === 'FORM') {
              if (this.openedDialog) {
                this.onFormConfigUpdateAsRequest.next(m.params);
              }
            } else if (this._zuperSdkEventService.isPreBuildModalIdAvailable(m.instanceGuid)) {
              this._zuperSdkEventService.castPreBuildFormModalPatch(m.params, m.instanceGuid, 'prop');
            }
          } else if (m.request === 'dispatch') {
            if (uiConfig?.type === 'MODAL' && uiConfig?.options?.dataType === 'IFRAME') {
              if (this.openedDialog) {
                const instanceId = this.openedDialog.componentInstance.data['instanceId'];
                if (m.instanceGuid === instanceId) {
                  this.dispatchDataForIframeModal.next(m.params);
                } else {
                  console.error('Error: dispatch can only work for opened dialog');
                }
              }
            }
          } else if (m.request === 'handle') {
            this._zuperSdkEventService.handleSdkListenerResponse(m.params);
          } else if (m.request === 'isExist') {
            this.isComponentExist(m);
          } else if (m.request === 'reset') {
            this.reset(m);
          }
        } else if (m.key && m.key.includes('pre_event')) {
          this._zuperSdkEventService.registerPreEventHandler(m.key);
        }
      }
    });
  }

  isUIComponentExist(id: string): boolean {
    return id in this.uiComponentsIdMap;
  }

  fetchCreatedButtons(location): any {
    const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot);
    try {
      return this.customButtonLocationMap[routeData['page']][location] || [];
    }
    catch (e) {
      return [];
    }
  }

  sendMessage(childWindow: Window | null, message: any): void {
    childWindow = childWindow || this.childWindow;
    try {
      // Create a serializable version of the message
      childWindow.postMessage(
        message,
        window.origin
      );
    } catch (error) {
      console.error('Failed to send message to child window:', error);
    }
  }

  invoke(data: any): void {
    const params = data.params;

    const actions = {
      'ui.create': (): void => this.createUIComponent(data),
      'ui.remove': (): void => this.removeUIComponent(data),
      'ui.hide': (): void => this.showOrHideUIComponent(data, false),
      'ui.show': (): void => this.showOrHideUIComponent(data, true),
      'ui.open': (): void => this.openUIComponent(data),
      'ui.close': (): void => this.removeUIComponent(data),
      'user.switch': (): void => this._zuperSdkEventService.onInvokeSwitchingAccounts(data),
      'page.navigate': (): void => this._zuperSdkRouteService.navigateToRoute(data),
      'page.refresh': (): void => this._zuperSdkEventService.onInvokePageRefresh(),
    };

    const actionKeys = Object.keys(actions);
    const matchingAction = actionKeys.find(key => key in params);
    if (matchingAction) {
      actions[matchingAction]();
    }
  }

  createButtonBasedOnLocation(data: any, buttonType = 'SINGLE'): void {
    const config = data.params['ui.create'][0];
    config['buttonType'] = buttonType;
    const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot);
    if (this.customButtonLocationMap[config.page]) {
      const existingButtons = this.customButtonLocationMap[config.page][config.location];
      if (existingButtons && existingButtons.length === 5) {
        this.onUICreated(data, config, `Error: Exceeding maximum button count in ${config.location} location`);
        return;
      }
    }
    switch (config.location) {
      case 'BREADCRUMB':
        if (routeData['page'] === config.page) {
          if (routeData['page'] === 'dashboard') {
            this.dashboardBreadcrumbEvent.next(
              { data, action: 'create' });
          } else {
            this.breadcrumbEvent.next(
              { data, action: 'create' });
          }
        } else {
          this.onUICreated(data, config);
        }
        break;
      case 'BULK_ACTION':
        if (!config['page'].includes('list')) {
          this.onUICreated(data, config, 'Error: Invalid page or Bulk Action can be created only in list pages');
          break;
        }
        if (buttonType !== 'SINGLE') {
          this.onUICreated(data, config, 'Error: Only button type can be created in Bulk Action location');
          break;
        }
        if (routeData['page'] === config.page) {
          this.snackBarEvent.next({ data, action: 'create' });
        }
        this.onUICreated(data, config);
        break;
      default:
        break;
    }
  }

  createDialog(message, type = 'DIALOG'): void {
    const dialogConfig = message.params['ui.create'][0];
    if (this.openedDialog) {
      this.onUICreated(message, dialogConfig, 'Error: dialog is already opened');
      return;
    }
    this.onUICreated(message, dialogConfig);
  }

  createToast(message): void {
    const toastConfig = message.params['ui.create'][0];
    const toastMessage = toastConfig.message;
    const toastOptions = toastConfig.options || {};
    toastOptions['id'] = 'sdk_toast_' + message['id'];
    this._toastService.show(toastMessage, toastOptions);
    const instanceId = this.onUICreated(message, toastConfig);
    if (toastOptions.autoClose && instanceId) {
      setTimeout(() => {
        this.removeUIComponent({
          instanceGuid: instanceId
        });
      }, (toastOptions.duration || 3000) + 100);
    }
  }

  createUIComponent(data: any): void {
    const config = data.params['ui.create'][0];
    if (this.uiComponentsIdMap[config.id]) {
      this.onUICreated(data, config, 'Error: Component with same id already created.');
      return;
    }
    switch (config.type) {
      case 'BUTTON':
        if (validateObject(config, buttonConfigValidator)) {
          this.createButtonBasedOnLocation(data);
        } else {
          this.onUICreated(data, config, 'Error: Invalid config');
        }
        break;
      case 'DROPDOWN':
        if (validateObject(config, dropDownConfigValidator)) {
          this.createButtonBasedOnLocation(data, 'DROPDOWN');
        } else {
          this.onUICreated(data, config, 'Error: Invalid config');
        }
        break;
      case 'DIALOG':
        if (validateObject(config, dialogConfigValidator)) {
          this.createDialog(data);
        } else {
          this.onUICreated(data, config, 'Error: Invalid config');
        }
        break;
      case 'MODAL':
        if (validateObject(config, modalConfigValidator)) {
          this.createDialog(data, 'MODAL');
        } else {
          this.onUICreated(data, config, 'Error: Invalid config');
        }
        break;
      case 'TOAST':
        if (validateObject(config, toastConfigValidator)) {
          this.createToast(data);
        } else {
          this.onUICreated(data, config, 'Error: Invalid config');
        }
        break;
      default:
        break;
    }
  }

  openUIComponent(data: any): void {
    const instanceId = data.instanceGuid;
    const uiComponent = this.uiComponentsCreated[instanceId];
    if (uiComponent) {
      const uiConfig = uiComponent.config;
      const type = uiConfig['type'];
      if (type === 'MODAL' || type === 'DIALOG') {
        this.openedDialog = type === 'MODAL' ? this._zuperDialogService.openCustom(SdkCustomDialogComponent, { ...uiConfig.options, instanceId: instanceId })
          : this._zuperDialogService.open({ ...uiConfig.options, instanceId: instanceId });
        this.onDialogOpened(uiConfig.id);
        this.onDialogClosed(uiConfig.id);
      }
    }
  }

  removeUIComponent(data: any): void {
    const instanceId = data.instanceGuid;
    const uiComponent = this.uiComponentsCreated[instanceId];
    if (uiComponent && uiComponent?.config) {
      const uiConfig = uiComponent.config;
      const location = uiConfig['location'];
      const page = uiConfig['page'];
      const type = uiConfig['type'];
      if (type === 'BUTTON' || type === 'DROPDOWN') {
        const currentRouteData = this._utilityService.collectRouteData(this._activatedRoute.snapshot);
        if (page === currentRouteData['page']) {
          this.performActionBasedonLocation(location, uiComponent, 'remove',
            type === 'DROPDOWN' ? data['params']['ui.remove'] || [] : null
          );
        }
        this.customButtonLocationMap[page][location] = this.customButtonLocationMap[page][location].filter(
          (i: any) => i.instanceGuid !== data.appGuid
        );
      } else if (type === 'DIALOG' || type === 'MODAL') {
        this.openedDialog.close();
        this.openedDialog = null;
      } else if (type === 'TOAST') {
        this._toastService.close(
          'sdk_toast_' + uiComponent['id']
        );
      }
      delete this.uiComponentsCreated[instanceId];
      delete this.uiComponentsIdMap[uiConfig.id];
    }
  }

  showOrHideUIComponent(data: any, show = true): void {
    const instanceId = data.instanceGuid;
    const uiComponent = this.uiComponentsCreated[instanceId];
    if (uiComponent) {
      const uiConfig = uiComponent.config;
      const location = uiConfig['location'];
      const page = uiConfig['page'];
      const type = uiConfig['type'];
      const currentRouteData = this._utilityService.collectRouteData(this._activatedRoute.snapshot);
      if (page !== currentRouteData['page']) {
        return;
      }
      switch (type) {
        case 'BUTTON':
          this.performActionBasedonLocation(location, uiComponent, show ? 'show' : 'hide');
          break;
        case 'DROPDOWN':
          const optionsArray = data['params'][show ? 'ui.show' : 'ui.hide'] || [];
          this.performActionBasedonLocation(location, uiComponent, show ? 'show' : 'hide', optionsArray);
          break;
        case 'DIALOG':
          if (this.openedDialog) {
            if (show) {
              this.openedDialog.removePanelClass('invisible');
            } else {
              this.openedDialog.addPanelClass('invisible');
            }
          }
          break;
        case 'MODAL':
          if (this.openedDialog) {
            if (show) {
              this.openedDialog.removePanelClass('invisible');
            } else {
              this.openedDialog.addPanelClass('invisible');
            }
          }
          break;
        default: break;
      }
    }
  }

  performActionBasedonLocation(location, data, action, args = []): void {
    switch (location) {
      case 'BREADCRUMB':
        const event = data.config.page === 'dashboard' ? this.dashboardBreadcrumbEvent : this.breadcrumbEvent;
        event.next(
          { data, action: action, args: args }
        );
        break;
      case 'BULK_ACTION':
        this.snackBarEvent.next(
          { data, action: action, args: args }
        );
        break;
      default: break;
    }
  }

  onUICreated(message: any, config: any, errorMessage?: string): string | null {
    const uuid = uuidv4();
    if (!errorMessage) {
      const data = {
        id: message.id,
        instanceGuid: message.instanceGuid,
        config: config,
        result: {
          uid: uuid,
          id: config.id
        }
      };
      this.uiComponentsCreated[uuid] = data;
      if (config.type === 'DROPDOWN') {
        config.children.forEach((child: any) => {
          this.uiComponentsIdMap[child.id] = uuid;
        });
      } else {
        this.uiComponentsIdMap[config.id] = uuid;
      }
      if (config.type === 'DROPDOWN' || config.type === 'BUTTON') {
        if (!(config.page in this.customButtonLocationMap)) {
          this.customButtonLocationMap[config.page] = {};
        }
        if (!(config.location in this.customButtonLocationMap[config.page])) {
          this.customButtonLocationMap[config.page][config.location] = [];
        }
        this.customButtonLocationMap[config.page][config.location] = [
          ...this.customButtonLocationMap[config.page][config.location],
          message
        ];
      }
    }
    this.sendMessage(this.childWindow, {
      id: message.id,
      key: 'get',
      instanceGuid: message.instanceGuid,
      result: errorMessage ? {
        error: errorMessage,
        id: config.id,
      } : {
        uid: uuid,
        id: config.id
      }
    });
    return errorMessage ? null : uuid;
  }

  onButtonClicked(message: any, config: any, payload?: any): void {
    const data = {
      id: message.id,
      key: 'zuper.click',
      instanceGuid: message.result.uid,
      message: message.config.type === 'DROPDOWN' ? {
        id: config.id,
        label: config.displayKey
      } : payload ? payload : 'button clicked',
      result: {
        uid: message.result.uid,
        id: config.id,
      }
    };
    this.sendMessage(this.childWindow, data);
  }

  onDialogOpened(id: any): void {
    const createdDialogId = this.uiComponentsIdMap[id];
    const dialogConfig = this.uiComponentsCreated[createdDialogId];
    const dialog = this.openedDialog as MatDialogRef<SdkCustomDialogComponent, any>;
    dialog.afterOpened().subscribe(() => {
      const response = {
        id: dialogConfig.id,
        key: 'zuper.loaded',
        instanceGuid: dialogConfig.result.uid,
        message: dialogConfig.config['type'] + ' loaded',
        result: {
          uid: dialogConfig.result.uid,
          id: id,
        },
      };
      this.sendMessage(this.childWindow, response);
    });
    if (dialogConfig.config.type === 'MODAL' && ['IFRAME', 'FORM'].includes(dialogConfig.config.options?.dataType)) {
      this.handleModalSubscription(dialogConfig, id, dialogConfig.config.options?.dataType);
    }
  }

  onDialogClosed(id: string | number): void {
    const createdDialogId = this.uiComponentsIdMap[id];
    const dialogConfig = this.uiComponentsCreated[createdDialogId];
    const dialog = this.openedDialog as MatDialogRef<SdkCustomDialogComponent, any>
    dialog.afterClosed().subscribe((data: any) => {
      const payload = data?.data || null;
      let message = payload;
      let response = {};
      if(dialogConfig.config.type === 'MODAL' && dialogConfig.config.options?.dataType === 'FORM'){
          message = payload;
          response = {
            id: dialogConfig.id,
            key: 'zuper.' + data.type,
            instanceGuid: dialogConfig.result.uid,
            message: message,
            result: {
              uid: dialogConfig.result.uid,
              id: id,
            },
          };
      }else{
        const type = data?.type ??  (data ? 'confirm' : 'cancel');
        message = 'dialog ' + type + 'ed';
        response = {
          id: dialogConfig.id,
          key: 'zuper.' + type,
          instanceGuid: dialogConfig.result.uid,
          message: message,
          result: {
            uid: dialogConfig.result.uid,
            id: id,
          },
        };
      }
      this.sendMessage(this.childWindow, response);
      // flushing closed components from the object
      delete this.uiComponentsIdMap?.[id];
      delete this.uiComponentsCreated?.[createdDialogId];
      this.openedDialog = null;
    });
    if (dialog?.componentInstance?.iframeEventEmitter) {
      dialog.componentInstance.iframeEventEmitter.subscribe((data: any) => {
        const response = {
          id: dialogConfig?.id,
          key: 'zuper.listen',
          instanceGuid: dialogConfig?.result?.uid,
          message: data,
          result: {
            uid: dialogConfig?.result?.uid,
            id: id,
          },
        };
        this.sendMessage(this.childWindow, response);
      });
    }
    if (dialog?.componentInstance?.confirmEventEmitter) {
      dialog.componentInstance.confirmEventEmitter.subscribe((data: any) => {
        const response = {
          id: dialogConfig?.id,
          key: 'zuper.confirm',
          instanceGuid: dialogConfig?.result?.uid,
          message: data,
          result: {
            uid: dialogConfig?.result?.uid,
            id: id,
          },
        };
        this.sendMessage(this.childWindow, response);
      });
    }
  }

  fetchRespectiveKeysFromObject(keys: string[], obj: any): any {
    const data = {};
    keys.forEach((key: string) => {
      if (obj[key]) {
        data[key] = obj[key];
      }
    });
    return data;
  }

  onBulkActionButtonClicked(id: string, data: any): void {
    const btnGuid = this.uiComponentsIdMap[id];
    const btnMessage = this.uiComponentsCreated[btnGuid];
    if (btnMessage) {
      this.onButtonClicked(btnMessage, btnMessage.config, data);
    }
  }

  isComponentExist(config: any): void {
    const id = config.params[0];
    const response = {
      id: config.id,
      key: 'zuper.isExist',
      instanceGuid: config.instanceGuid,
      message: this.uiComponentsIdMap[id],
      result: this.uiComponentsIdMap[id] ? {
        uid: this.uiComponentsIdMap[id],
        id: id
      } : {
        error: 'No component created or existed with mentioned id',
        id: id
      }
    };
    this.sendMessage(this.childWindow, response);
  }

  reset(m?: any): void {
    Object.keys(this.uiComponentsIdMap).forEach((id: string) => {
      const instanceID = this.uiComponentsIdMap[id];
      if (instanceID) {
        this.removeUIComponent({
          instanceGuid: instanceID
        });
      }
    });
    this.uiComponentsCreated = {};
    this.uiComponentsIdMap = {};
    this.customButtonLocationMap = {};
    if (m) {
      this.sendMessage(
        this.childWindow,
        {
          id: null,
          key: 'zuper.app.reset',
          instanceGuid: m.instanceGuid,
          message: 'App got reset!'
        });
    }
  }

  private handleModalSubscription(dialogConfig: any, id: string, type: 'IFRAME' | 'FORM'): void {
    const subject$ = type === 'IFRAME' ? this.onCustomDialogIframeLoad : this.onFormDialogChanges;
    const eventKey = type === 'IFRAME' ? 'zuper.iframe.load' : 'zuper.changes';
    const message = type === 'IFRAME' ? 'iframe loaded' : null; // Form will use data as message

    const sub$ = subject$.subscribe((data: any) => {
      if (data) {
        const response = {
          id: dialogConfig.id,
          key: eventKey,
          instanceGuid: dialogConfig.result.uid,
          message: message || data,
          result: {
            uid: dialogConfig.result.uid,
            id: id,
          },
        };
        this.sendMessage(this.childWindow, response);
      }
    });
    
    this.$subscriptions.push(sub$);
  }
}
